// CatalogSyncPanel'in görünümü (durum üst bileşende — SSR testi için saf render). İki kip:
// • eski bant (paket satırı yok): "Katalogda N güncelleme var" + tablo — markup birebir aynı;
// • paket kipi (DR7): kapalı özet "<paket>: 2 eklendi · 1 çıkarıldı · Katalog: 1 formül", açık başlık
//   "<paket>: 2 ekleme önerisi · …" (yalnız eyleme açık), paket satırları ayrı bölüm ("Sete al"),
//   bilgi satırları kapalı "Bilgi (N)" alt bölümü; yalnız bilgi kaldıysa amber bant yerine tek satır;
//   "Tümünü uygula (2 ekleme · 1 formül)" yalnız eyleme açık satırları uygular; sonuç `aria-live`.

import type { KpiCatalogChange, KpiCatalogSyncStatus, KpiMetric } from "@mahir/shared";
import {
  KPI_CATALOG_SYNC_COPY as COPY,
  applyAllLabel,
  breakdownSuggestion,
  closedBandSummary,
  describeCatalogChange,
  groupCatalogChanges,
  openBandTitle,
  packageRowText,
} from "../../lib/kpi-catalog-sync";
import { KPI_PACKAGES_COPY } from "../../lib/kpi-packages-copy";
import {
  amberBox,
  btnGhost,
  btnPrimary,
  btnSmall,
  codeStyle,
  footerBtn,
  tdStyle,
  thStyle,
} from "./kpi-form-styles";

const B = KPI_PACKAGES_COPY.band;

export interface CatalogSyncBandProps {
  status: KpiCatalogSyncStatus;
  canEdit: boolean;
  blocked: boolean;
  /** uygulanan satır anahtarı ("all" = tümü) */
  applying: string | null;
  /** `${kind}:${metricId}` → hata metni */
  failed: Readonly<Record<string, string>>;
  /** "Hepsini yoksay" imzası eşleşiyor */
  dismissed: boolean;
  open: boolean;
  infoOpen: boolean;
  /** son uygulamanın iki parçalı sonucu (paket kipi) */
  result: string | null;
  catalogById: ReadonlyMap<string, KpiMetric>;
  onToggleOpen: () => void;
  onToggleInfo: () => void;
  onApply: (items: KpiCatalogChange[], tag: string) => void;
  onDismissAll: () => void;
  onRestore: () => void;
  onOpenIndicator?: ((indicatorId: string) => void) | undefined;
}

export const changeKey = (c: KpiCatalogChange): string => `${c.kind}:${c.metricId}`;

export function CatalogSyncBand(props: CatalogSyncBandProps): JSX.Element | null {
  const { status, canEdit, blocked, applying, open } = props;
  const groups = groupCatalogChanges(status.changes);
  const n = groups.actionable.length;
  if (groups.packageMode && n === 0) return <InfoOnlyLine {...props} info={groups.info} />;
  if (n === 0) return null;

  if (props.dismissed) {
    return (
      <div role="status" className="text-xs mb-3" style={{ color: "var(--color-text-2)" }}>
        {COPY.dismissed} ({n.toString()}).{" "}
        <button
          type="button"
          className="underline"
          style={{ color: "var(--color-primary)", cursor: "pointer" }}
          onClick={props.onRestore}
        >
          {COPY.restore}
        </button>
      </div>
    );
  }

  const applyDisabled = !canEdit || blocked || applying !== null;
  const title = groups.packageMode
    ? open
      ? openBandTitle(status.changes)
      : closedBandSummary(status.changes)
    : null;

  return (
    <section
      aria-label={title ?? COPY.band(n)}
      className="rounded-lg mb-3"
      style={{ ...amberBox, padding: 0, overflow: "hidden" }}
      data-testid="kpi-catalog-sync-band"
    >
      <div
        className="flex items-center gap-3 flex-wrap"
        style={{ padding: "10px 12px", minHeight: 44 }}
      >
        {/* Finar 15.2: flex-basis 0 metni düğmelerden önce 3-4 karaktere eziyordu; metin en az 260 px
            ister, düğmeler kendi sarılan grubunda ikinci satıra düşer. */}
        <div className="min-w-0" style={{ flex: "1 1 260px" }}>
          {title !== null ? (
            <b role="status" className="text-sm" style={{ color: "var(--color-warning-text)" }}>
              🔄 {title}
            </b>
          ) : (
            <b className="text-sm" style={{ color: "var(--color-warning-text)" }}>
              🔄 {COPY.band(n)}
            </b>
          )}
          <div className="text-xs" style={{ color: "var(--color-text-2)" }}>
            {blocked && canEdit ? COPY.blockedHint : COPY.bandHint}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap" style={{ flex: "0 1 auto" }}>
          <button type="button" style={btnSmall} aria-expanded={open} onClick={props.onToggleOpen}>
            {open ? COPY.hide : COPY.show}
          </button>
          {canEdit ? (
            <>
              <button
                type="button"
                style={{ ...btnPrimary, opacity: applyDisabled ? 0.6 : 1 }}
                disabled={applyDisabled}
                title={blocked ? COPY.blockedHint : undefined}
                onClick={() => props.onApply(groups.actionable, "all")}
              >
                {applying === "all"
                  ? COPY.applying
                  : groups.packageMode
                    ? applyAllLabel(groups)
                    : COPY.applyAll}
              </button>
              <button
                type="button"
                style={btnGhost}
                title={COPY.dismissAllTitle}
                disabled={applying !== null}
                onClick={props.onDismissAll}
              >
                {COPY.dismissAll}
              </button>
            </>
          ) : null}
        </div>
      </div>
      {groups.packageMode && props.result ? (
        <div
          aria-live="polite"
          className="text-sm"
          style={{ padding: "0 12px 10px", color: "var(--color-text)" }}
        >
          {props.result}
        </div>
      ) : null}
      {open ? (
        <div style={{ background: "var(--color-surface)", overflowX: "auto" }}>
          {groups.catalogRows.length > 0 ? (
            <CatalogRowsTable {...props} rows={groups.catalogRows} applyDisabled={applyDisabled} />
          ) : null}
          {groups.packageRows.length > 0 ? (
            <PackageRows {...props} rows={groups.packageRows} applyDisabled={applyDisabled} />
          ) : null}
          {groups.info.length > 0 ? <InfoSection {...props} info={groups.info} /> : null}
        </div>
      ) : null}
    </section>
  );
}

function CatalogRowsTable(
  props: CatalogSyncBandProps & { rows: KpiCatalogChange[]; applyDisabled: boolean },
): JSX.Element {
  const { canEdit, blocked, applying, failed, rows, applyDisabled } = props;
  return (
    <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <th style={thStyle}>{COPY.columns.indicator}</th>
          <th style={thStyle}>{COPY.columns.kind}</th>
          <th style={thStyle}>{COPY.columns.catalog}</th>
          <th style={thStyle}>{COPY.columns.set}</th>
          {canEdit ? <th style={thStyle} /> : null}
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => {
          const bd = breakdownSuggestion(c, props.catalogById);
          const d = bd ? { ...describeCatalogChange(c), ...bd } : describeCatalogChange(c);
          const k = changeKey(c);
          const err = failed[k];
          return (
            <tr key={k}>
              <td style={tdStyle}>
                <div>{c.metricLabel}</div>
                <code style={codeStyle}>{c.metricCode}</code>
              </td>
              <td style={tdStyle} title={bd?.hint ?? COPY.kindHint[c.kind]}>
                {d.kind}
                {c.packageId && (c.kind === "formula" || c.kind === "unit") ? (
                  <div className="text-xs" style={{ color: "var(--color-text-2)" }}>
                    {B.valueChange}
                  </div>
                ) : null}
              </td>
              <td style={tdStyle}>
                {c.kind === "formula" ? (
                  <code style={codeStyle}>{d.catalogValue}</code>
                ) : (
                  d.catalogValue
                )}
              </td>
              <td style={tdStyle}>
                {c.kind === "formula" && c.setValue ? (
                  <code style={codeStyle}>{d.setValue}</code>
                ) : (
                  d.setValue
                )}
                {c.kind === "unit" && c.setValue ? (
                  <div className="text-xs" style={{ color: "var(--color-text-2)" }}>
                    ⓘ {COPY.unitClientInfo(c.setValue, d.catalogValue)}
                  </div>
                ) : null}
                {err ? (
                  <div role="alert" className="text-xs" style={{ color: "var(--color-error)" }}>
                    {err}
                    {c.kind === "unit" && c.indicatorId && props.onOpenIndicator ? (
                      <button
                        type="button"
                        style={{ ...btnSmall, marginLeft: 8 }}
                        onClick={() => props.onOpenIndicator?.(c.indicatorId!)}
                      >
                        {COPY.openIndicator}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </td>
              {canEdit ? (
                <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                  <button
                    type="button"
                    style={btnSmall}
                    disabled={applyDisabled}
                    title={blocked ? COPY.blockedHint : (bd?.hint ?? COPY.kindHint[c.kind])}
                    onClick={() => props.onApply([c], k)}
                  >
                    {applying === k ? COPY.applying : COPY.apply}
                  </button>
                </td>
              ) : null}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** Paket ekleme satırları — Katalog/Set kolonları yok; satır başına "Sete al". */
function PackageRows(
  props: CatalogSyncBandProps & { rows: KpiCatalogChange[]; applyDisabled: boolean },
): JSX.Element {
  const { canEdit, blocked, applying, failed, rows, applyDisabled } = props;
  const byPackage = new Map<string, KpiCatalogChange[]>();
  for (const c of rows) {
    const name = c.packageName ?? B.unnamedPackage;
    byPackage.set(name, [...(byPackage.get(name) ?? []), c]);
  }
  return (
    <section
      aria-label={B.packageSection}
      data-testid="kpi-catalog-sync-packages"
      style={{ padding: "8px 12px", borderTop: "1px solid var(--color-border)" }}
    >
      {[...byPackage].map(([name, list]) => (
        <div key={name} style={{ marginBottom: 6 }}>
          <div className="text-xs" style={{ color: "var(--color-text-2)", fontWeight: 600 }}>
            {name}
          </div>
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {list.map((c) => {
              const k = changeKey(c);
              return (
                <li
                  key={k}
                  className="flex items-center gap-2 flex-wrap"
                  style={{ minHeight: 44, color: "var(--color-text)", fontSize: "0.875rem" }}
                >
                  <span style={{ flex: "1 1 240px" }}>{packageRowText(c)}</span>
                  {canEdit ? (
                    <button
                      type="button"
                      style={footerBtn()}
                      disabled={applyDisabled}
                      title={blocked ? COPY.blockedHint : COPY.kindHint[c.kind]}
                      onClick={() => props.onApply([c], k)}
                    >
                      {applying === k ? B.applying : B.takeToSet}
                    </button>
                  ) : null}
                  {failed[k] ? (
                    <span
                      role="alert"
                      className="text-xs"
                      style={{ color: "var(--color-error)", flexBasis: "100%" }}
                    >
                      {failed[k]}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
}

/** Kapalı başlayan "Bilgi (N)" alt bölümü — paketten çıkarılan göstergeler (eylem yok). */
function InfoSection(props: CatalogSyncBandProps & { info: KpiCatalogChange[] }): JSX.Element {
  return (
    <div style={{ padding: "6px 12px 10px", borderTop: "1px solid var(--color-border)" }}>
      <InfoToggle {...props} />
      {props.infoOpen ? <InfoList info={props.info} /> : null}
    </div>
  );
}

function InfoToggle(props: CatalogSyncBandProps & { info: KpiCatalogChange[] }): JSX.Element {
  return (
    <button
      type="button"
      aria-expanded={props.infoOpen}
      onClick={props.onToggleInfo}
      style={{
        border: 0,
        background: "transparent",
        color: "var(--color-primary-text)",
        cursor: "pointer",
        minHeight: 44,
        padding: 0,
        fontSize: "0.8125rem",
        fontWeight: 600,
      }}
    >
      {props.infoOpen ? "▾" : "▸"} {B.infoTitle(props.info.length)}
    </button>
  );
}

function InfoList({ info }: { info: KpiCatalogChange[] }): JSX.Element {
  return (
    <ul
      data-testid="kpi-catalog-sync-info"
      style={{ listStyle: "none", margin: 0, padding: 0, color: "var(--color-text-3)" }}
      className="text-xs"
    >
      {info.map((c) => (
        <li key={changeKey(c)} style={{ padding: "2px 0" }}>
          {B.removedInfo(c.metricLabel)}
        </li>
      ))}
    </ul>
  );
}

/** Yalnız bilgi satırı kaldı: amber bant yok, tek `text-3` satır + açılır "Bilgi (N)". */
function InfoOnlyLine(props: CatalogSyncBandProps & { info: KpiCatalogChange[] }): JSX.Element {
  return (
    <div
      role="status"
      data-testid="kpi-catalog-sync-info-only"
      className="text-xs mb-3"
      style={{ color: "var(--color-text-3)" }}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span>{B.onlyInfo}</span>
        <InfoToggle {...props} />
      </div>
      {props.infoOpen ? <InfoList info={props.info} /> : null}
    </div>
  );
}
